import { useEffect, useState } from "react";
import { useData } from "../contexts/DataContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { RichTextEditor } from "../components/ui/RichTextEditor";
import { VariableInsertButton } from "../components/ui/VariableInsertButton";
import { toast } from "sonner";
import {
  Plus, Loader2, Trash2, Edit, FileStack, Copy, Star, ChevronDown, ChevronUp,
  ArrowLeft, Save, GripVertical, Code, Type, FileText, Film, Layout,
  Settings2, Layers, DollarSign, Percent, Send, CheckCircle, Eye, EyeOff,
  ArrowUp, ArrowDown, ImagePlus, Columns, Minus, Link, GitCompare
} from "lucide-react";

// Refactored components
import { PAGE_TYPES, DEFAULT_SETTINGS } from "../components/quote-templates/constants";
import { ContentBlockEditor } from "../components/quote-templates/ContentBlocks";
import { CoverPageEditor, GalleryPageEditor, FilePageEditor } from "../components/quote-templates/PageEditors";
import { PagePreviewRenderer } from "../components/quote-templates/PagePreviewRenderer";
import { LineItemsEditor } from "../components/quote-templates/LineItemsEditor";

// ======================== BLOCK TYPES for custom pages ========================
const BLOCK_TYPES = [
  { value: "text", label: "Text", icon: Type },
  { value: "columns", label: "Columns", icon: Columns },
  { value: "spacer", label: "Spacer / Break", icon: Minus },
  { value: "image", label: "Image", icon: ImagePlus },
  { value: "before_after", label: "Before & After", icon: GitCompare },
  { value: "video", label: "Video", icon: Film },
  { value: "html", label: "HTML / Embed Code", icon: Code },
  { value: "link", label: "Link / Button", icon: Link },
];

// ======================== PAGE EDITOR ========================

function PageEditor({ page, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast, index, allPages, dragHandleProps, priceList, onMoveGroupToPage, onClonePricing, quoteSettings }) {
  const [expanded, setExpanded] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const content = page.content || {};
  const updateContent = (key, val) => onChange({ ...page, content: { ...content, [key]: val } });
  const updateTitle = (title) => onChange({ ...page, title });
  const pageInfo = PAGE_TYPES.find(p => p.value === page.page_type) || PAGE_TYPES[0];
  const Icon = pageInfo.icon;

  const blocks = content.blocks || [];
  const addBlock = (type) => {
    const base = { id: `blk-${Date.now()}`, type, content: "", fontFamily: "Inter", fontSize: "14", color: "#1e293b", textAlign: "left", fontWeight: "normal", fontStyle: "normal", textDecoration: "none", height: 20, url: "", alt: "", label: "", width: "full" };
    if (type === "columns") { base.columns = 2; base.column_data = [{ content: "" }, { content: "" }]; }
    if (type === "before_after") { base.before_url = ""; base.after_url = ""; base.before_label = "Before"; base.after_label = "After"; base.layout = "side_by_side"; base.caption = ""; }
    updateContent("blocks", [...blocks, base]);
  };
  const updateBlock = (idx, updated) => updateContent("blocks", blocks.map((b, i) => i === idx ? updated : b));
  const removeBlock = (idx) => updateContent("blocks", blocks.filter((_, i) => i !== idx));
  const moveBlock = (idx, dir) => {
    const arr = [...blocks];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    updateContent("blocks", arr);
  };

  return (
    <div className="border-2 border-slate-200 rounded-lg overflow-hidden" data-testid={`template-page-${index}`}>
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-slate-100 to-slate-50 cursor-pointer border-b border-slate-200" onClick={() => setExpanded(!expanded)}>
        <div {...(dragHandleProps || {})} className="cursor-grab active:cursor-grabbing" onClick={e => e.stopPropagation()}>
          <GripVertical className="w-4 h-4 text-slate-400 hover:text-slate-600 flex-shrink-0" />
        </div>
        <div className="w-6 h-6 rounded-full bg-[#0F4C81] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{index + 1}</div>
        <div className={`w-7 h-7 rounded flex items-center justify-center ${pageInfo.color}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="flex-1 text-sm font-semibold text-slate-800">{page.title || pageInfo.label}</span>
        <Badge variant="secondary" className="text-[10px] font-bold">{pageInfo.label}</Badge>
        <div className="flex gap-0.5">
          <Button type="button" variant="ghost" size="sm" className={`h-7 px-2 text-xs ${previewMode ? "bg-[#0F4C81] text-white hover:bg-[#0D4270]" : ""}`} onClick={e => { e.stopPropagation(); setPreviewMode(!previewMode); if (!expanded) setExpanded(true); }} data-testid={`preview-page-${index}`}>
            {previewMode ? <><EyeOff className="w-3 h-3 mr-1" />Edit</> : <><Eye className="w-3 h-3 mr-1" />Preview</>}
          </Button>
          {!isFirst && <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); onMoveUp(); }}><ArrowUp className="w-3.5 h-3.5" /></Button>}
          {!isLast && <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); onMoveDown(); }}><ArrowDown className="w-3.5 h-3.5" /></Button>}
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={e => { e.stopPropagation(); onRemove(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </div>
      {expanded && (
        <div className="p-4 space-y-3 border-t border-slate-100">
          {previewMode ? (
            <PagePreviewRenderer page={page} allPages={allPages} settings={quoteSettings} />
          ) : (
          <>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Page Title</Label>
            <Input value={page.title || ""} onChange={e => updateTitle(e.target.value)} className="h-9 text-sm" placeholder="Page title" />
          </div>

          {(page.page_type === "structure" || page.page_type === "gallery" || page.page_type === "custom" || page.page_type === "addon") && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Mobile Layout</Label>
              <Select
                value={page.mobile_layout || "auto"}
                onValueChange={v => onChange({ ...page, mobile_layout: v })}
              >
                <SelectTrigger className="h-9 text-sm" data-testid={`page-mobile-layout-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (use template default)</SelectItem>
                  <SelectItem value="single">Single Column</SelectItem>
                  <SelectItem value="double">Two Columns</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">Controls how this page&apos;s cards stack on phones.</p>
            </div>
          )}

          {page.page_type === "cover" && <CoverPageEditor content={content} updateContent={updateContent} />}

          {page.page_type === "option" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs font-semibold">Option Name *</Label><Input value={content.option_name || ""} onChange={e => updateContent("option_name", e.target.value)} className="h-9 text-sm" placeholder="e.g., IKO Dynasty Architectural" data-testid="option-name-input" /></div>
                <div className="space-y-1"><Label className="text-xs font-semibold">Option Tag</Label><Input value={content.option_tag || ""} onChange={e => updateContent("option_tag", e.target.value)} className="h-9 text-sm" placeholder="e.g., Most Affordable, Top Dog" /></div>
              </div>

              {/* ============================================
                  Linked-Option Pricing — moved to the TOP of
                  the option editor because it's a rule most
                  people forget to set. Big amber card so it's
                  unmissable.
                  ============================================ */}
              {(() => {
                const otherOptions = (allPages || []).filter(p => p.page_type === "option" && p.id !== page.id);
                if (otherOptions.length === 0) return null;
                const linkedId = content.linked_to_option_id || "";
                const linkedTarget = otherOptions.find(p => p.id === linkedId);
                const linkedActive = !!linkedId && !content.linked_to_option_disabled;
                return (
                  <div
                    className={`rounded-lg p-3 space-y-2 transition-colors ${linkedActive
                      ? "border-2 border-emerald-400 bg-emerald-50"
                      : "border-2 border-amber-300 bg-amber-50/60"}`}
                    data-testid={`template-option-link-config-${page.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white font-bold text-xs ${linkedActive ? "bg-emerald-500" : "bg-amber-500"}`}>
                          {linkedActive ? "\u2713" : "\u26A1"}
                        </span>
                        <div>
                          <Label className={`text-sm font-bold ${linkedActive ? "text-emerald-800" : "text-amber-900"}`}>
                            {linkedActive ? "Price Linked" : "Lock This Option's Price To Another?"}
                          </Label>
                          <p className={`text-[11px] ${linkedActive ? "text-emerald-700" : "text-amber-700"} mt-0.5`}>
                            {linkedActive
                              ? "This option's total is calculated from the linked option."
                              : "Optional. Great for pairs like \"IKO Dynasty always $100 less than Tamko Titan\"."}
                          </p>
                        </div>
                      </div>
                      {linkedId && (
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer whitespace-nowrap font-semibold">
                          <input
                            type="checkbox"
                            checked={!content.linked_to_option_disabled}
                            onChange={(e) => updateContent("linked_to_option_disabled", !e.target.checked)}
                            className="h-4 w-4"
                            data-testid={`template-option-link-toggle-${page.id}`}
                          />
                          <span>Active</span>
                        </label>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2 space-y-1">
                        <Label className="text-[10px] text-slate-600 uppercase tracking-wide">Link to which option?</Label>
                        <select
                          value={linkedId}
                          onChange={(e) => updateContent("linked_to_option_id", e.target.value)}
                          className="w-full h-10 rounded border border-slate-300 px-2 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/40"
                          data-testid={`template-option-link-target-${page.id}`}
                        >
                          <option value="">— No link (independent pricing) —</option>
                          {otherOptions.map(op => (
                            <option key={op.id} value={op.id}>
                              {(op.content?.option_name) || op.title || "Untitled option"}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-slate-600 uppercase tracking-wide">Offset $</Label>
                        <Input
                          type="number"
                          step="1"
                          value={content.linked_offset ?? ""}
                          onChange={(e) => updateContent("linked_offset", e.target.value === "" ? "" : parseFloat(e.target.value))}
                          placeholder="-100"
                          disabled={!linkedId}
                          className="h-10 text-sm"
                          title="Positive to add, negative to subtract."
                          data-testid={`template-option-link-offset-${page.id}`}
                        />
                      </div>
                    </div>
                    {linkedActive && linkedTarget && (
                      <p className="text-[12px] text-emerald-900 bg-white/80 border border-emerald-200 rounded px-2 py-1.5 font-medium">
                        Price = <b>{(linkedTarget.content?.option_name) || linkedTarget.title || "target"}</b>
                        {Number.isFinite(parseFloat(content.linked_offset)) && ` ${parseFloat(content.linked_offset) >= 0 ? "+" : "−"} $${Math.abs(parseFloat(content.linked_offset)).toLocaleString()}`}
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Highlights */}
              <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2 mb-1">
                  <div className="w-1 h-4 rounded bg-[#00A3E0]" />
                  <Label className="text-xs font-bold uppercase text-[#00A3E0] tracking-wide">Highlights / Description</Label>
                </div>
                <RichTextEditor value={content.description || ""} onChange={v => updateContent("description", v)} rows={3} placeholder="Key features, warranty info, selling points..." testId="template-option-description" />
              </div>

              <div className="flex items-center gap-3 py-1"><div className="flex-1 border-t-2 border-dashed border-slate-200" /><span className="text-[9px] uppercase text-slate-300 font-bold tracking-widest">auto line break</span><div className="flex-1 border-t-2 border-dashed border-slate-200" /></div>

              {/* Scope */}
              <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2 mb-1">
                  <div className="w-1 h-4 rounded bg-amber-500" />
                  <Label className="text-xs font-bold uppercase text-amber-600 tracking-wide">Scope of Work</Label>
                </div>
                <RichTextEditor value={content.scope || ""} onChange={v => updateContent("scope", v)} rows={4} placeholder="1. Remove existing shingles&#10;2. Inspect decking&#10;3. Install underlayment..." testId="template-option-scope" />
              </div>

              <div className="flex items-center gap-3 py-1"><div className="flex-1 border-t-2 border-dashed border-slate-200" /><span className="text-[9px] uppercase text-slate-300 font-bold tracking-widest">auto line break</span><div className="flex-1 border-t-2 border-dashed border-slate-200" /></div>

              {/* Important Info */}
              <div className="border border-amber-200 rounded-lg p-3 bg-amber-50/30 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-4 rounded bg-amber-400" />
                  <Label className="text-xs font-bold uppercase text-amber-600 tracking-wide">Important Information</Label>
                </div>
                <RichTextEditor value={content.important_info || ""} onChange={v => updateContent("important_info", v)} rows={3} placeholder="Decking replacement policy, payment terms..." testId="template-option-important-info" />
              </div>

              <div className="flex items-center gap-3 py-1"><div className="flex-1 border-t-2 border-dashed border-slate-200" /><span className="text-[9px] uppercase text-slate-300 font-bold tracking-widest">auto line break</span><div className="flex-1 border-t-2 border-dashed border-slate-200" /></div>

              {/* Pricing & Warranties */}
              <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2 mb-1">
                  <div className="w-1 h-4 rounded bg-emerald-500" />
                  <Label className="text-xs font-bold uppercase text-emerald-600 tracking-wide">Pricing & Warranties</Label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Base Price ($)</Label><Input type="number" value={content.base_price || ""} onChange={e => updateContent("base_price", e.target.value)} className="h-9 text-sm" placeholder="0.00" /></div>
                  <div className="space-y-1"><Label className="text-xs">Warranty (Material)</Label><Input value={content.warranty_material || ""} onChange={e => updateContent("warranty_material", e.target.value)} className="h-9 text-sm" placeholder="Limited Lifetime" /></div>
                  <div className="space-y-1"><Label className="text-xs">Warranty (Wind)</Label><Input value={content.warranty_wind || ""} onChange={e => updateContent("warranty_wind", e.target.value)} className="h-9 text-sm" placeholder="130 mph" /></div>
                </div>
              </div>

              {/* Linked-Option Pricing card moved to the TOP of the option editor */}

              <div className="flex flex-wrap gap-4 pt-1">
                {[["show_price","Show Price"],["show_scope","Show Scope"],["show_warranty","Show Warranties"],["show_discounts","Apply Discounts"]].map(([k,l])=>(
                  <label key={k} className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={content[k]!==false} onChange={e=>updateContent(k,e.target.checked)} className="rounded" />{l}</label>
                ))}
              </div>

              {/* Clone Pricing Button */}
              {onClonePricing && (content.line_items?.length > 0 || content.structures?.length > 0) && (() => {
                const otherOptions = (allPages || []).filter(p => p.page_type === "option" && p.id !== page.id);
                if (otherOptions.length === 0) return null;
                const thisOptionName = content.option_name || page.title || "this option";
                return (
                  <div className="border border-dashed border-red-300 rounded-lg p-3 bg-red-50/40">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[220px]">
                        <p className="text-xs font-bold text-red-700">Copy Pricing FROM <span className="text-[#0F4C81]">{thisOptionName}</span> →</p>
                        <p className="text-[10px] text-red-700/80 mt-1 leading-relaxed">
                          <b>⚠ Careful:</b> This copies every line item &amp; group into the target — including brand-specific products.
                          If your options use different materials (e.g. IKO vs Tamko shingles),
                          material orders on the target will show the wrong brand.
                          For same-math-different-price setups, use <b>Lock This Option&apos;s Price To Another</b> at the top instead.
                        </p>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {otherOptions.map(op => (
                          <Button key={op.id} type="button" variant="outline" size="sm" className="h-7 text-[10px]"
                            onClick={() => {
                              if (window.confirm(`Copy pricing FROM "${thisOptionName}" TO "${op.content?.option_name || op.title || "target"}"?\n\nThis will REPLACE all line items and structures on the target option. If they use different materials this will overwrite them.`)) {
                                onClonePricing(page.id, op.id);
                              }
                            }}
                            data-testid={`clone-to-${op.id}`}
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            → {op.content?.option_name || op.title || "Option"}
                          </Button>
                        ))}
                        <Button type="button" variant="default" size="sm" className="h-7 text-[10px] bg-[#0F4C81]"
                          onClick={() => {
                            if (window.confirm(`Copy pricing FROM "${thisOptionName}" TO ALL other options?\n\nThis will REPLACE every other option's line items with a copy of this one's. Only do this if all options use the same materials.`)) {
                              otherOptions.forEach(op => onClonePricing(page.id, op.id));
                            }
                          }}
                          data-testid="clone-to-all"
                        >
                          <Copy className="w-3 h-3 mr-1" />→ All Others
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <LineItemsEditor
                lineItems={content.line_items} structures={content.structures} priceList={priceList}
                showCashDiscount={content.show_cash_discount} onToggleCashDiscount={v => updateContent("show_cash_discount", v)}
                taxRate={content.tax_rate || 0} onTaxRateChange={v => updateContent("tax_rate", v)}
                allOptionPages={(allPages || []).filter(p => p.page_type === "option" || p.page_type === "addon")}
                currentPageId={page.id}
                onMoveGroupToPage={onMoveGroupToPage ? (groupId, targetPageId) => onMoveGroupToPage(page.id, groupId, targetPageId) : undefined}
                onChange={({ line_items, structures }) => { onChange({ ...page, content: { ...content, line_items, structures } }); }}
              />
            </div>
          )}

          {page.page_type === "addon" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Add-on Name</Label><Input value={content.addon_name || ""} onChange={e => updateContent("addon_name", e.target.value)} className="h-9 text-sm" placeholder="e.g., Extended Warranty Upgrade" /></div>
                <div className="space-y-1"><Label className="text-xs">Price ($)</Label><Input type="number" value={content.price || ""} onChange={e => updateContent("price", e.target.value)} className="h-9 text-sm" placeholder="0.00" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Description</Label><RichTextEditor value={content.description || ""} onChange={v => updateContent("description", v)} rows={3} placeholder="What this add-on includes..." testId="template-addon-description" /></div>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={content.show_price!==false} onChange={e=>updateContent("show_price",e.target.checked)} className="rounded" />Show Price</label>
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={content.apply_discount===true} onChange={e=>updateContent("apply_discount",e.target.checked)} className="rounded" />Apply Discount</label>
              </div>
              <LineItemsEditor
                lineItems={content.line_items} structures={content.structures} priceList={priceList}
                showCashDiscount={content.show_cash_discount} onToggleCashDiscount={v => updateContent("show_cash_discount", v)}
                taxRate={content.tax_rate || 0} onTaxRateChange={v => updateContent("tax_rate", v)}
                allOptionPages={(allPages || []).filter(p => p.page_type === "option" || p.page_type === "addon")}
                currentPageId={page.id}
                onMoveGroupToPage={onMoveGroupToPage ? (groupId, targetPageId) => onMoveGroupToPage(page.id, groupId, targetPageId) : undefined}
                onChange={({ line_items, structures }) => { onChange({ ...page, content: { ...content, line_items, structures } }); }}
              />
            </div>
          )}

          {page.page_type === "text" && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Section Title</Label><Input value={content.title || ""} onChange={e => updateContent("title", e.target.value)} className="h-9 text-sm" placeholder="Terms & Conditions" /></div>
              <div className="space-y-1"><Label className="text-xs">Body Text</Label><Textarea value={content.body || ""} onChange={e => updateContent("body", e.target.value)} rows={8} className="text-sm" placeholder="Enter terms, warranty info, or any text content..." /></div>
            </div>
          )}

          {page.page_type === "custom" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Content Blocks</Label>
                <div className="flex gap-1">
                  {BLOCK_TYPES.map(bt => (
                    <Button key={bt.value} type="button" variant="outline" size="sm" className="h-7 text-[10px] px-2" onClick={() => addBlock(bt.value)} title={bt.label}>
                      <bt.icon className="w-3 h-3 mr-1" />{bt.label}
                    </Button>
                  ))}
                </div>
              </div>
              {blocks.length > 0 ? (
                <div className="space-y-2">
                  {blocks.map((block, i) => (
                    <ContentBlockEditor key={block.id} block={block} onChange={updated => updateBlock(i, updated)} onRemove={() => removeBlock(i)} onMoveUp={() => moveBlock(i, -1)} onMoveDown={() => moveBlock(i, 1)} isFirst={i === 0} isLast={i === blocks.length - 1} />
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                  <Layout className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                  <p className="text-xs text-slate-400">Add content blocks above: text, images, video, HTML, spacers, links</p>
                </div>
              )}
            </div>
          )}

          {page.page_type === "gallery" && <GalleryPageEditor content={content} updateContent={updateContent} />}
          {page.page_type === "file" && <FilePageEditor content={content} updateContent={updateContent} />}

          {page.page_type === "financing" && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Financing Page Title</Label>
                <Input value={content.title || ""} onChange={e => updateContent("title", e.target.value)} className="h-9 text-sm" placeholder="Financing Options" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Intro Text</Label>
                <Textarea value={content.intro || ""} onChange={e => updateContent("intro", e.target.value)} rows={2} className="text-sm" placeholder="We've partnered with trusted lenders to offer flexible financing..." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-green-700">HFS Financial</p>
                  <div className="space-y-1"><Label className="text-[10px]">Prequal Link</Label><Input value={content.hfs_link || ""} onChange={e => updateContent("hfs_link", e.target.value)} className="h-8 text-xs" placeholder="https://www.hfsfinancial.net/promo/..." /></div>
                  <div className="space-y-1"><Label className="text-[10px]">Description</Label><Textarea value={content.hfs_desc || ""} onChange={e => updateContent("hfs_desc", e.target.value)} rows={2} className="text-xs" placeholder="Funded directly to you, the homeowner. Soft pull to prequalify." /></div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-blue-700">Wisetack</p>
                  <div className="space-y-1"><Label className="text-[10px]">Prequal Link</Label><Input value={content.wisetack_link || ""} onChange={e => updateContent("wisetack_link", e.target.value)} className="h-8 text-xs" placeholder="https://wisetack.us/#/.../prequalify" /></div>
                  <div className="space-y-1"><Label className="text-[10px]">Description</Label><Textarea value={content.wisetack_desc || ""} onChange={e => updateContent("wisetack_desc", e.target.value)} rows={2} className="text-xs" placeholder="Funded directly to your contractor. Soft pull to prequalify." /></div>
                </div>
              </div>
              <label className="flex items-center gap-1.5 text-xs">
                <input type="checkbox" checked={content.show_calculator !== false} onChange={e => updateContent("show_calculator", e.target.checked)} className="rounded" />
                Show Wisetack Payment Calculator
              </label>
            </div>
          )}

          {page.page_type === "signature" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Customer Signature Label</Label><Input value={content.customer_label || ""} onChange={e => updateContent("customer_label", e.target.value)} className="h-9 text-sm" placeholder="Customer Signature" /></div>
                <div className="space-y-1"><Label className="text-xs">Company Signature Label</Label><Input value={content.admin_label || ""} onChange={e => updateContent("admin_label", e.target.value)} className="h-9 text-sm" placeholder="Company Signature" /></div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Signed Confirmation Message</Label>
                <div className="flex items-center gap-2">
                  <p className="text-[10px] text-slate-400">Shown after customer signs.</p>
                  <VariableInsertButton onInsert={(v) => updateContent("signed_message", (content.signed_message || "") + v)} />
                </div>
                <Textarea value={content.signed_message || ""} onChange={e => updateContent("signed_message", e.target.value)} rows={2} className="text-sm" placeholder="Thank you {{first_name}}! We look forward to working with you." />
              </div>
              <div className="flex flex-wrap gap-4">
                {[["show_date","Show Date"],["show_printed_name","Printed Name"],["show_options","Option Selection"],["show_addons","Add-on Selection"],["show_summary","Price Summary"],["show_questions","Final Questions"],["show_terms","Terms & Conditions"]].map(([k,l])=>(
                  <label key={k} className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={content[k]!==false} onChange={e=>updateContent(k,e.target.checked)} className="rounded" />{l}</label>
                ))}
              </div>
              <div className="space-y-1"><Label className="text-xs">Final Questions (one per line)</Label><Textarea value={content.questions || ""} onChange={e => updateContent("questions", e.target.value)} rows={3} className="text-sm" placeholder="What color shingle do you prefer?&#10;Do you want white or black drip edge?" /></div>
              <div className="space-y-1">
                <Label className="text-xs">Terms & Conditions</Label>
                <p className="text-[10px] text-slate-400">Shown as a collapsible section above the signature. Customer must read before signing.</p>
                <Textarea value={content.terms_and_conditions || ""} onChange={e => updateContent("terms_and_conditions", e.target.value)} rows={6} className="text-sm font-mono" placeholder="Enter your terms and conditions..." />
              </div>
            </div>
          )}

          {page.page_type === "structure" && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Structure Name</Label><Input value={content.title || ""} onChange={e => updateContent("title", e.target.value)} className="h-9 text-sm" placeholder="e.g., Main House, Garage, Detached Shed" /></div>
              <div className="space-y-1">
                <Label className="text-xs">Structure Notes</Label>
                <p className="text-[10px] text-slate-400">Important info visible to customers for all options under this structure.</p>
                <RichTextEditor value={content.notes || ""} onChange={v => updateContent("notes", v)} rows={3} placeholder="e.g., Decking replacement billed separately at $75/sheet if damage found during tear-off..." testId="template-structure-notes" />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700 font-medium">Options after this page belong to this structure. Customers pick one option per structure.</p>
                <p className="text-[10px] text-blue-600 mt-0.5">Line items added here are <b>shared</b> across all options under this structure.</p>
              </div>
              <LineItemsEditor
                lineItems={content.line_items} structures={content.structures} priceList={priceList}
                showCashDiscount={content.show_cash_discount} onToggleCashDiscount={v => updateContent("show_cash_discount", v)}
                taxRate={content.tax_rate || 0} onTaxRateChange={v => updateContent("tax_rate", v)}
                onChange={({ line_items, structures }) => { onChange({ ...page, content: { ...content, line_items, structures } }); }}
              />
            </div>
          )}

          {page.page_type === "form" && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Form Title</Label><Input value={content.title || ""} onChange={e => updateContent("title", e.target.value)} className="h-9 text-sm" placeholder="Customer Preferences" /></div>
              <div className="space-y-1"><Label className="text-xs">Form Fields (one per line, format: Label|type)</Label><Textarea value={content.fields || ""} onChange={e => updateContent("fields", e.target.value)} rows={4} className="text-sm font-mono" placeholder="Preferred shingle color|text&#10;Start date preference|date&#10;Special requests|textarea" /></div>
            </div>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
}

// ======================== SETTINGS TAB ========================

// ===================== SIGNING QUESTIONS EDITOR =====================
//
// Edits the array of customer-facing questions that appear on the View & Sign
// page above the signature block. Each question has:
//   id, label, type ("text"|"textarea"|"choice"), options (for choice),
//   required, order, help_text, dynamic_links (optional brand-color helpers).
//
export function SigningQuestionsEditor({ questions, onChange }) {
  const addQuestion = (type = "text") => {
    const next = {
      id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      label: type === "textarea" ? "Notes" : type === "choice" ? "Choose one" : "New question",
      type,
      options: type === "choice" ? ["Option A", "Option B"] : [],
      required: false,
      order: questions.length,
      help_text: "",
      dynamic_links: [],
    };
    onChange([...questions, next]);
  };
  const update = (idx, patch) => onChange(questions.map((q, i) => i === idx ? { ...q, ...patch } : q));
  const remove = (idx) => onChange(questions.filter((_, i) => i !== idx).map((q, i) => ({ ...q, order: i })));
  const move = (idx, dir) => {
    const arr = [...questions];
    const t = idx + dir;
    if (t < 0 || t >= arr.length) return;
    [arr[idx], arr[t]] = [arr[t], arr[idx]];
    onChange(arr.map((q, i) => ({ ...q, order: i })));
  };

  return (
    <Card data-testid="signing-questions-editor">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Customer Questions on Sign Page</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Questions the customer answers when reviewing & signing. Required fields block submission until answered.</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={() => addQuestion("text")} data-testid="add-question-text"><Plus className="w-3.5 h-3.5 mr-1" />Text</Button>
            <Button size="sm" variant="outline" onClick={() => addQuestion("textarea")} data-testid="add-question-textarea"><Plus className="w-3.5 h-3.5 mr-1" />Notes</Button>
            <Button size="sm" variant="outline" onClick={() => addQuestion("choice")} data-testid="add-question-choice"><Plus className="w-3.5 h-3.5 mr-1" />Choice</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {(!questions || questions.length === 0) && (
          <p className="text-sm text-slate-400 italic text-center py-4">No customer questions yet. Add one above.</p>
        )}
        {questions.map((q, idx) => (
          <SigningQuestionRow
            key={q.id}
            question={q}
            isFirst={idx === 0}
            isLast={idx === questions.length - 1}
            onChange={(patch) => update(idx, patch)}
            onRemove={() => remove(idx)}
            onMove={(dir) => move(idx, dir)}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function SigningQuestionRow({ question, isFirst, isLast, onChange, onRemove, onMove }) {
  const [showAdv, setShowAdv] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`question-row-${question.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-0.5 pt-1">
          <button type="button" onClick={() => onMove(-1)} disabled={isFirst} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up">▲</button>
          <button type="button" onClick={() => onMove(1)} disabled={isLast} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down">▼</button>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={question.label}
              onChange={(e) => onChange({ label: e.target.value })}
              placeholder="Question label (e.g., What color shingle?)"
              className="h-9 text-sm flex-1"
              data-testid={`q-label-${question.id}`}
            />
            <select
              value={question.type}
              onChange={(e) => onChange({ type: e.target.value })}
              className="h-9 text-sm border border-slate-300 rounded-md px-2 bg-white"
              data-testid={`q-type-${question.id}`}
            >
              <option value="text">Short answer</option>
              <option value="textarea">Long notes</option>
              <option value="choice">Choice</option>
            </select>
            <label className="inline-flex items-center gap-1.5 text-xs px-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!question.required}
                onChange={(e) => onChange({ required: e.target.checked })}
                data-testid={`q-required-${question.id}`}
              />
              <span className="font-medium text-slate-600">Required</span>
            </label>
          </div>
          {question.type === "choice" && (
            <Input
              value={(question.options || []).join(", ")}
              onChange={(e) => onChange({ options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
              placeholder="Comma-separated options (e.g., White, Black)"
              className="h-9 text-sm"
              data-testid={`q-options-${question.id}`}
            />
          )}
          <Input
            value={question.help_text || ""}
            onChange={(e) => onChange({ help_text: e.target.value })}
            placeholder="Optional help text shown under the question"
            className="h-8 text-xs"
            data-testid={`q-help-${question.id}`}
          />
          <button
            type="button"
            onClick={() => setShowAdv(s => !s)}
            className="text-xs text-[#0F4C81] hover:underline"
          >{showAdv ? "Hide" : "Add"} brand-specific help links {(question.dynamic_links || []).length > 0 && `(${question.dynamic_links.length})`}</button>
          {showAdv && (
            <div className="pl-3 border-l-2 border-slate-300 space-y-2">
              <p className="text-[11px] text-slate-500 italic">Show a help link only when the selected option's name/tag matches the keyword (case-insensitive, use | for OR).</p>
              {(question.dynamic_links || []).map((dl, di) => (
                <div key={di} className="grid grid-cols-1 sm:grid-cols-12 gap-1.5 items-start">
                  <Input value={dl.match || ""} onChange={(e) => onChange({ dynamic_links: question.dynamic_links.map((x, i) => i === di ? { ...x, match: e.target.value } : x) })} placeholder="iko|dynasty" className="h-8 text-xs sm:col-span-2" />
                  <Input value={dl.label || ""} onChange={(e) => onChange({ dynamic_links: question.dynamic_links.map((x, i) => i === di ? { ...x, label: e.target.value } : x) })} placeholder="Browse IKO colors" className="h-8 text-xs sm:col-span-3" />
                  <Input value={dl.url || ""} onChange={(e) => onChange({ dynamic_links: question.dynamic_links.map((x, i) => i === di ? { ...x, url: e.target.value } : x) })} placeholder="https://..." className="h-8 text-xs sm:col-span-4" />
                  <Input value={dl.note || ""} onChange={(e) => onChange({ dynamic_links: question.dynamic_links.map((x, i) => i === di ? { ...x, note: e.target.value } : x) })} placeholder="Optional note (e.g., premium colors add $500)" className="h-8 text-xs sm:col-span-2" />
                  <button type="button" onClick={() => onChange({ dynamic_links: question.dynamic_links.filter((_, i) => i !== di) })} className="text-red-500 hover:text-red-700 text-xs sm:col-span-1">Remove</button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => onChange({ dynamic_links: [...(question.dynamic_links || []), { match: "", label: "", url: "", note: "" }] })}>
                <Plus className="w-3 h-3 mr-1" />Add link
              </Button>
            </div>
          )}
        </div>
        <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700 text-xs pt-1 px-1" title="Remove question" data-testid={`q-remove-${question.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


function SettingsTab({ settings, onChange, pipelineStages, pipelineCategories }) {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  const update = (key, val) => onChange({ ...s, [key]: val });
  const stagesByCategory = pipelineCategories.map(cat => ({ ...cat, stages: pipelineStages.filter(st => st.category === cat.value) })).filter(cat => cat.stages.length > 0);
  const addDiscount = () => update("discounts", [...(s.discounts || []), { name: "", type: "percent", value: 0 }]);
  const updateDiscount = (idx, field, val) => update("discounts", s.discounts.map((d, i) => i === idx ? { ...d, [field]: val } : d));
  const removeDiscount = (idx) => update("discounts", s.discounts.filter((_, i) => i !== idx));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Settings2 className="w-4 h-4 text-[#0F4C81]" />Quote Defaults</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1"><Label className="text-xs text-slate-500">Default Quote Name</Label><Input value={s.default_quote_name} onChange={e => update("default_quote_name", e.target.value)} className="h-9 text-sm" placeholder="e.g., Roof Install Estimate" data-testid="settings-quote-name" /></div>
          <div className="flex items-center justify-between py-2">
            <div><Label className="text-sm">Show Quote Summary</Label><p className="text-xs text-slate-400">Display summary on quote document</p></div>
            <Switch checked={s.show_quote_summary} onCheckedChange={v => update("show_quote_summary", v)} />
          </div>
          <div className="space-y-1"><Label className="text-xs text-slate-500">Quote Expiration (days)</Label><Input type="number" value={s.quote_expiration_days ?? ''} onChange={e => update("quote_expiration_days", e.target.value === '' ? '' : parseInt(e.target.value))} className="h-9 w-32 text-sm" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Percent className="w-4 h-4 text-[#0F4C81]" />Pricing & Margins</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1"><Label className="text-xs text-slate-500">Profit Margin %</Label><Input type="number" min="0" max="100" value={s.profit_margin ?? ''} onChange={e => update("profit_margin", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-9 text-sm" data-testid="settings-profit-margin" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500">Deposit %</Label><Input type="number" min="0" max="100" value={s.deposit_percent ?? ''} onChange={e => update("deposit_percent", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-9 text-sm" data-testid="settings-deposit" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500">Rounding</Label>
              <Select value={s.rounding} onValueChange={v => update("rounding", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exact</SelectItem>
                  <SelectItem value="round_up">Round Up</SelectItem>
                  <SelectItem value="round_nearest">Nearest Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-slate-500">Default Discounts</Label>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addDiscount}><Plus className="w-3 h-3 mr-1" />Add</Button>
            </div>
            {(s.discounts || []).map((disc, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Input value={disc.name} onChange={e => updateDiscount(i, "name", e.target.value)} className="h-8 text-sm flex-1" placeholder="Discount name" />
                <Select value={disc.type} onValueChange={v => updateDiscount(i, "type", v)}>
                  <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">%</SelectItem><SelectItem value="fixed">$</SelectItem></SelectContent>
                </Select>
                <Input type="number" value={disc.value ?? ''} onChange={e => updateDiscount(i, "value", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-8 w-20 text-sm" />
                <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400" onClick={() => removeDiscount(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Send className="w-4 h-4 text-[#0F4C81]" />Send Quote Actions</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Move project to stage when quote is sent</Label>
            <Select value={s.send_quote_stage || "none"} onValueChange={v => update("send_quote_stage", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Don't change stage" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't change stage</SelectItem>
                {stagesByCategory.map(cat => (<div key={cat.value}><div className="px-2 py-1 text-xs font-bold text-slate-400 uppercase">{cat.name}</div>{cat.stages.map(st => <SelectItem key={st.value} value={st.value}>{st.name}</SelectItem>)}</div>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Primary Signer Message</Label>
              <VariableInsertButton onInsert={(v) => update("primary_signer_message", (s.primary_signer_message || "") + v)} />
            </div>
            <Textarea value={s.primary_signer_message} onChange={e => update("primary_signer_message", e.target.value)} rows={2} className="text-sm" placeholder="Thank you {{first_name}} for reviewing our estimate..." />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Secondary Signer Message</Label>
              <VariableInsertButton onInsert={(v) => update("secondary_signer_message", (s.secondary_signer_message || "") + v)} />
            </div>
            <Textarea value={s.secondary_signer_message} onChange={e => update("secondary_signer_message", e.target.value)} rows={2} className="text-sm" placeholder="A quote has been shared with you..." />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#0F4C81]" />Signed Quote Actions</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Move project to stage when partially signed</Label>
            <Select value={s.partially_signed_stage || "none"} onValueChange={v => update("partially_signed_stage", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Don't change stage" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't change stage</SelectItem>
                {stagesByCategory.map(cat => (<div key={cat.value}><div className="px-2 py-1 text-xs font-bold text-slate-400 uppercase">{cat.name}</div>{cat.stages.map(st => <SelectItem key={st.value} value={st.value}>{st.name}</SelectItem>)}</div>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Move project to stage when fully signed</Label>
            <Select value={s.signed_quote_stage || "none"} onValueChange={v => update("signed_quote_stage", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Don't change stage" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't change stage</SelectItem>
                {stagesByCategory.map(cat => (<div key={cat.value}><div className="px-2 py-1 text-xs font-bold text-slate-400 uppercase">{cat.name}</div>{cat.stages.map(st => <SelectItem key={st.value} value={st.value}>{st.name}</SelectItem>)}</div>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between py-2">
            <div><Label className="text-sm">Auto-Send Deposit Invoice</Label><p className="text-xs text-slate-400">Automatically send deposit invoice when quote is fully signed</p></div>
            <Switch checked={s.auto_send_deposit_invoice} onCheckedChange={v => update("auto_send_deposit_invoice", v)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#0F4C81]" />Financing</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div><Label className="text-sm">Include Financing Offer</Label><p className="text-xs text-slate-400">Show financing options on the quote</p></div>
            <Switch checked={s.financing_offer} onCheckedChange={v => update("financing_offer", v)} />
          </div>
          {s.financing_offer && (
            <div className="space-y-1"><Label className="text-xs text-slate-500">Financing Text</Label><Textarea value={s.financing_text} onChange={e => update("financing_text", e.target.value)} rows={2} className="text-sm" placeholder="Prequalify for financing today!" /></div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Layout className="w-4 h-4 text-[#0F4C81]" />Mobile Layout</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-sm">Compact Mobile Layout</Label>
              <p className="text-xs text-slate-400">Show option &amp; add-on cards in two columns on phones so the quote isn&apos;t a long skinny scroll. Pricing summaries stay full width.</p>
            </div>
            <Switch
              checked={!!s.compact_mobile_layout}
              onCheckedChange={v => update("compact_mobile_layout", v)}
              data-testid="settings-compact-mobile-layout"
            />
          </div>
          <p className="text-[11px] text-slate-400">Tip: individual pages can override this from the page editor.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ======================== TEMPLATE EDITOR (FULL PAGE) ========================

function TemplateEditor({ template, onSave, onClose, pipelineStages, pipelineCategories, serviceTypes, priceList, quoteSettings }) {
  const [formData, setFormData] = useState({
    name: template?.name || "",
    description: template?.description || "",
    service_type: template?.service_type || "",
    is_default: template?.is_default || false,
    pages: template?.pages || [],
    settings: { ...DEFAULT_SETTINGS, ...(template?.settings || {}) },
    email_body: template?.email_body || "",
    signing_questions: template?.signing_questions || [],
  });
  const [saving, setSaving] = useState(false);
  const [showAddPage, setShowAddPage] = useState(false);

  const handleSave = async () => {
    if (!formData.name) { toast.error("Template name is required"); return; }
    setSaving(true);
    try { await onSave(formData); toast.success(template ? "Template saved!" : "Template created!"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const addPage = (pageType) => {
    const defaults = {
      cover: { headline: "PROPOSAL", subheadline: "Prepared for:", bgColor: "#0F4C81", show_logo: true, show_date: true, show_quote_number: true, show_contact_info: true, show_company_info: true },
      option: { option_name: "", option_tag: "", description: "", scope: "", important_info: "", base_price: "", warranty_material: "", warranty_wind: "", show_price: true, show_scope: true, show_warranty: true, show_discounts: true, tax_rate: 9, show_cash_discount: true },
      addon: { addon_name: "", price: "", description: "", show_price: true, apply_discount: false, tax_rate: 9, show_cash_discount: true },
      text: { title: "", body: "" },
      custom: { blocks: [] },
      gallery: { title: "Past Projects", image_urls: "", layout: "grid" },
      file: { file_name: "", file_url: "", description: "" },
      signature: { customer_label: "Customer Signature", admin_label: "Company Signature", show_date: true, show_printed_name: true, show_options: true, show_addons: true, show_summary: true, show_questions: true, questions: "", show_terms: true, terms_and_conditions: "" },
      structure: { title: "", notes: "", line_items: [], structures: [], tax_rate: 0, show_cash_discount: true },
      form: { title: "Customer Preferences", fields: "" },
    };
    setFormData(prev => ({
      ...prev,
      pages: [...prev.pages, { id: `page-${Date.now()}`, page_type: pageType, title: PAGE_TYPES.find(p => p.value === pageType)?.label || pageType, content: defaults[pageType] || {}, order: prev.pages.length }]
    }));
    setShowAddPage(false);
  };

  const updatePage = (idx, updated) => setFormData(prev => ({ ...prev, pages: prev.pages.map((p, i) => i === idx ? updated : p) }));
  const removePage = (idx) => setFormData(prev => ({ ...prev, pages: prev.pages.filter((_, i) => i !== idx) }));
  const movePage = (idx, dir) => {
    setFormData(prev => {
      const arr = [...prev.pages]; const target = idx + dir;
      if (target < 0 || target >= arr.length) return prev;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...prev, pages: arr };
    });
  };

  const moveGroupToPage = (sourcePageId, groupId, targetPageId) => {
    setFormData(prev => {
      const pages = prev.pages.map(p => ({ ...p, content: { ...p.content } }));
      const srcIdx = pages.findIndex(p => p.id === sourcePageId);
      const tgtIdx = pages.findIndex(p => p.id === targetPageId);
      if (srcIdx === -1 || tgtIdx === -1) return prev;
      const srcContent = pages[srcIdx].content; const tgtContent = pages[tgtIdx].content;
      const srcGroups = [...(srcContent.structures || [])]; const srcItems = [...(srcContent.line_items || [])];
      const tgtGroups = [...(tgtContent.structures || [])]; const tgtItems = [...(tgtContent.line_items || [])];
      const groupIdx = srcGroups.findIndex(g => g.id === groupId);
      if (groupIdx === -1) return prev;
      const [movedGroup] = srcGroups.splice(groupIdx, 1);
      const itemsToMove = srcItems.filter(it => it.structure_group === groupId);
      const remainingItems = srcItems.filter(it => it.structure_group !== groupId);
      tgtGroups.push(movedGroup); tgtItems.push(...itemsToMove);
      pages[srcIdx].content = { ...srcContent, structures: srcGroups, line_items: remainingItems };
      pages[tgtIdx].content = { ...tgtContent, structures: tgtGroups, line_items: tgtItems };
      return { ...prev, pages };
    });
  };

  // Clone line items + structures from one option page to another
  const clonePricing = (sourcePageId, targetPageId) => {
    setFormData(prev => {
      const pages = prev.pages.map(p => ({ ...p, content: { ...p.content } }));
      const srcIdx = pages.findIndex(p => p.id === sourcePageId);
      const tgtIdx = pages.findIndex(p => p.id === targetPageId);
      if (srcIdx === -1 || tgtIdx === -1) return prev;
      const srcContent = pages[srcIdx].content;
      // Deep clone structures with new IDs
      const idMap = {};
      const clonedGroups = (srcContent.structures || []).map(g => {
        const newId = `sg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        idMap[g.id] = newId;
        return { ...g, id: newId };
      });
      // Deep clone items with new IDs and remapped group refs
      const clonedItems = (srcContent.line_items || []).map(it => ({
        ...it,
        id: `li_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        structure_group: idMap[it.structure_group] || it.structure_group || "",
      }));
      pages[tgtIdx].content = {
        ...pages[tgtIdx].content,
        structures: clonedGroups,
        line_items: clonedItems,
        tax_rate: srcContent.tax_rate,
        show_cash_discount: srcContent.show_cash_discount,
      };
      return { ...prev, pages };
    });
    toast.success("Pricing cloned!");
  };

  const settings = formData.settings || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose} className="h-9" data-testid="back-to-templates"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
          <div className="h-5 w-px bg-slate-200" />
          <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="h-9 text-sm font-semibold border-0 bg-transparent focus-visible:ring-1 w-64" placeholder="Template Name *" data-testid="editor-template-name" />
          <Select value={formData.service_type || "all"} onValueChange={v => setFormData({ ...formData, service_type: v === "all" ? "" : v })}>
            <SelectTrigger className="h-9 w-36 text-sm"><SelectValue placeholder="All services" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All services</SelectItem>
              {serviceTypes.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs mr-3"><input type="checkbox" checked={formData.is_default} onChange={e => setFormData({ ...formData, is_default: e.target.checked })} className="rounded" />Default Template</label>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" data-testid="save-template-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <Tabs defaultValue="pages" className="w-full">
          <TabsList className="bg-white border border-slate-200 h-11 p-1 rounded-lg mb-6">
            <TabsTrigger value="pages" className="text-sm px-6 data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white rounded-md" data-testid="editor-tab-pages"><Layers className="w-4 h-4 mr-2" />Page Builder</TabsTrigger>
            <TabsTrigger value="preview" className="text-sm px-6 data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white rounded-md" data-testid="editor-tab-preview"><Eye className="w-4 h-4 mr-2" />Preview</TabsTrigger>
            <TabsTrigger value="settings" className="text-sm px-6 data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white rounded-md" data-testid="editor-tab-settings"><Settings2 className="w-4 h-4 mr-2" />Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="pages">
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-white"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-slate-800">{formData.pages.length}</p><p className="text-xs text-slate-400">Pages</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-slate-800">{settings.deposit_percent || 50}%</p><p className="text-xs text-slate-400">Deposit</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-slate-800">{(settings.discounts || []).length}</p><p className="text-xs text-slate-400">Discounts</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-slate-800">{settings.financing_offer ? "Yes" : "No"}</p><p className="text-xs text-slate-400">Financing</p></CardContent></Card>
            </div>

            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={() => setShowAddPage(true)} data-testid="add-page-btn"><Plus className="w-4 h-4 mr-2" />Add Page</Button>
            </div>

            {formData.pages.length > 0 ? (
              <DragDropContext onDragEnd={(result) => {
                if (!result.destination) return;
                const from = result.source.index; const to = result.destination.index;
                if (from === to) return;
                setFormData(prev => { const pages = [...prev.pages]; const [moved] = pages.splice(from, 1); pages.splice(to, 0, moved); return { ...prev, pages }; });
              }}>
                <Droppable droppableId="template-pages">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                      {formData.pages.map((page, index) => {
                        // Determine if this page is nested under a structure
                        let isNested = false;
                        if (page.page_type === "option" || page.page_type === "addon") {
                          for (let i = index - 1; i >= 0; i--) {
                            if (formData.pages[i].page_type === "structure") { isNested = true; break; }
                            if (formData.pages[i].page_type !== "option" && formData.pages[i].page_type !== "addon") break;
                          }
                        }
                        return (
                        <Draggable key={page.id} draggableId={page.id} index={index}>
                          {(dragProvided, snapshot) => (
                            <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} style={dragProvided.draggableProps.style} className={`${snapshot.isDragging ? "opacity-90 shadow-lg rounded-lg" : ""} ${isNested ? "ml-8 border-l-4 border-indigo-200 rounded-l-none" : ""}`}>
                              <PageEditor page={page} index={index} allPages={formData.pages} priceList={priceList}
                                onChange={updated => updatePage(index, updated)} onRemove={() => removePage(index)}
                                onMoveUp={() => movePage(index, -1)} onMoveDown={() => movePage(index, 1)}
                                isFirst={index === 0} isLast={index === formData.pages.length - 1}
                                dragHandleProps={dragProvided.dragHandleProps} onMoveGroupToPage={moveGroupToPage}
                                onClonePricing={clonePricing} quoteSettings={quoteSettings} />
                            </div>
                          )}
                        </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">No pages yet. Click "Add Page" to start building your template.</p>
              </div>
            )}

            <Card className="mt-6">
              <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Send className="w-4 h-4 text-[#0F4C81]" />Email Body Template</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-2">
                  <VariableInsertButton onInsert={(v) => setFormData({ ...formData, email_body: (formData.email_body || "") + v })} />
                </div>
                <Textarea value={formData.email_body || ""} onChange={e => setFormData({ ...formData, email_body: e.target.value })}
                  placeholder={"Hello {{first_name}},\n\nPlease review the attached estimate for your project at {{project_address}}.\n\nBest regards,\n{{company_name}}"} rows={4} className="text-sm" />
                <p className="text-xs text-slate-400 mt-1">Variables: {"{{first_name}}"}, {"{{customer_name}}"}, {"{{company_name}}"}, {"{{quote_number}}"}, {"{{total}}"}, {"{{project_address}}"}</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preview">
            <div className="max-w-2xl mx-auto">
              {formData.pages.length > 0 ? (
                <div className="bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden" data-testid="full-template-preview">
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2"><Eye className="w-4 h-4 text-[#0F4C81]" /><span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Document Preview</span></div>
                    <span className="text-xs text-slate-400">{formData.pages.length} page{formData.pages.length !== 1 ? "s" : ""}</span>
                  </div>
                  {formData.pages.map((pg, idx) => {
                    const pgInfo = PAGE_TYPES.find(p => p.value === pg.page_type);
                    const isOption = pg.page_type === "option"; const isAddon = pg.page_type === "addon";
                    return (
                      <div key={pg.id}>
                        {idx > 0 && (
                          <div className="mx-6 my-3 flex items-center gap-3">
                            <div className="flex-1 border-t-2 border-slate-200" />
                            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-widest ${isOption ? "bg-[#0F4C81]/10 text-[#0F4C81]" : isAddon ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                              <span>Page {idx + 1}</span>
                            </div>
                            <div className="flex-1 border-t-2 border-slate-200" />
                          </div>
                        )}
                        <div className={`px-8 py-6 ${isOption ? "bg-slate-50/50" : isAddon ? "bg-emerald-50/20" : ""}`}>
                          {pg.page_type !== "cover" && !isOption && !isAddon && (
                            <div className="flex items-center gap-2 mb-3 opacity-40">
                              <div className="w-1 h-4 rounded bg-[#0F4C81]" />
                              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{pgInfo?.label || pg.page_type} — {pg.title || pgInfo?.label}</span>
                            </div>
                          )}
                          <PagePreviewRenderer page={pg} allPages={formData.pages} settings={quoteSettings} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-lg">
                  <Eye className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">No pages to preview. Add pages in the Page Builder tab.</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab settings={formData.settings} onChange={newSettings => setFormData({ ...formData, settings: newSettings })} pipelineStages={pipelineStages} pipelineCategories={pipelineCategories} />
            <div className="mt-6">
              <SigningQuestionsEditor
                questions={formData.signing_questions || []}
                onChange={(qs) => setFormData({ ...formData, signing_questions: qs })}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showAddPage} onOpenChange={setShowAddPage}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Page to Template</DialogTitle>
            <DialogDescription>Choose a page type to add</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {PAGE_TYPES.map(pt => (
              <button key={pt.value} onClick={() => addPage(pt.value)} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#0F4C81] hover:bg-[#0F4C81]/5 transition-colors text-left" data-testid={`add-page-${pt.value}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${pt.color}`}><pt.icon className="w-4 h-4" /></div>
                <div><p className="text-sm font-medium text-slate-700">{pt.label}</p><p className="text-[10px] text-slate-400">{pt.desc}</p></div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ======================== MAIN PAGE (Template List) ========================

export default function QuoteTemplates() {
  const {
    quoteTemplates, fetchQuoteTemplates, createQuoteTemplate,
    updateQuoteTemplate, deleteQuoteTemplate, dropdownOptions,
    fetchDropdownOptions, pipelineStages, fetchPipelineStages,
    pipelineCategories, fetchPipelineCategories, loading,
    priceList, fetchPriceList, quoteSettings, fetchQuoteSettings
  } = useData();

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchQuoteTemplates(); fetchDropdownOptions(); fetchPipelineStages(); fetchPipelineCategories(); fetchPriceList(); fetchQuoteSettings();
  }, [fetchQuoteTemplates, fetchDropdownOptions, fetchPipelineStages, fetchPipelineCategories, fetchPriceList, fetchQuoteSettings]);

  const serviceTypes = dropdownOptions.service_types || [];
  const handleEdit = (template) => { setEditingTemplate(template); setIsEditing(true); };
  const handleCreate = () => { setEditingTemplate(null); setIsCreating(true); };
  const handleCloseEditor = () => { setIsEditing(false); setIsCreating(false); setEditingTemplate(null); };

  const handleSave = async (data) => {
    if (editingTemplate) { await updateQuoteTemplate(editingTemplate.id, data); }
    else { await createQuoteTemplate(data); }
    handleCloseEditor();
  };

  const handleDuplicate = async (template) => {
    try { await createQuoteTemplate({ ...template, name: `${template.name} (Copy)`, is_default: false }); toast.success("Template duplicated!"); }
    catch { toast.error("Failed to duplicate"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this template?")) {
      try { await deleteQuoteTemplate(id); toast.success("Moved to trash"); }
      catch { toast.error("Failed to delete"); }
    }
  };

  if (isEditing || isCreating) {
    return <TemplateEditor template={editingTemplate} onSave={handleSave} onClose={handleCloseEditor} pipelineStages={pipelineStages} pipelineCategories={pipelineCategories} serviceTypes={serviceTypes} priceList={priceList} quoteSettings={quoteSettings} />;
  }

  return (
    <div className="min-h-screen">
      <Header title="Quote Templates">
        <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6" onClick={handleCreate} data-testid="create-template-btn"><Plus className="w-5 h-5 mr-2" />New Template</Button>
      </Header>
      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>
        ) : quoteTemplates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quoteTemplates.map(template => {
              const s = template.settings || {};
              return (
                <Card key={template.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleEdit(template)} data-testid={`template-card-${template.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-[#0F4C81]/10 rounded-lg"><FileStack className="w-5 h-5 text-[#0F4C81]" /></div>
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">{template.name}{template.is_default && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}</CardTitle>
                          {template.service_type && <Badge variant="secondary" className="mt-1 text-xs capitalize">{serviceTypes.find(st => st.value === template.service_type)?.label || template.service_type}</Badge>}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {template.description && <p className="text-sm text-slate-500 mb-3">{template.description}</p>}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="text-center p-2 bg-slate-50 rounded"><p className="text-lg font-bold text-slate-700">{template.pages?.length || 0}</p><p className="text-[10px] text-slate-400">Pages</p></div>
                      <div className="text-center p-2 bg-slate-50 rounded"><p className="text-lg font-bold text-slate-700">{s.deposit_percent || 50}%</p><p className="text-[10px] text-slate-400">Deposit</p></div>
                      <div className="text-center p-2 bg-slate-50 rounded"><p className="text-lg font-bold text-slate-700">{s.profit_margin || 40}%</p><p className="text-[10px] text-slate-400">Margin</p></div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(template.pages || []).slice(0, 5).map((page, idx) => {
                        const pt = PAGE_TYPES.find(p => p.value === page.page_type);
                        return <Badge key={idx} className={`text-[10px] ${pt?.color || "bg-slate-100 text-slate-600"}`}>{page.title || pt?.label}</Badge>;
                      })}
                      {(template.pages || []).length > 5 && <Badge variant="outline" className="text-[10px]">+{template.pages.length - 5}</Badge>}
                    </div>
                    <div className="flex gap-2 pt-3 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(template)} data-testid={`edit-template-${template.id}`}><Edit className="w-4 h-4 mr-1" />Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(template)}><Copy className="w-4 h-4 mr-1" />Duplicate</Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(template.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4"><FileStack className="w-8 h-8 text-slate-400" /></div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">No templates yet</h3>
            <p className="text-slate-500 mb-4">Create reusable quote templates for your estimates</p>
            <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={handleCreate}><Plus className="w-5 h-5 mr-2" />Create Template</Button>
          </div>
        )}
      </div>
    </div>
  );
}
